import type { StateStore } from 'oidc-client-ts';

/**
 * Returns Office add-in runtime storage when the API is available.
 * Persists across host documents (e.g. SharePoint / Excel on the web) unlike partitioned localStorage.
 */
function getOfficeRuntimeStorage(): OfficeRuntime.Storage | null {
  try {
    if (typeof OfficeRuntime === 'undefined' || !OfficeRuntime?.storage) {
      return null;
    }
    return OfficeRuntime.storage;
  } catch {
    return null;
  }
}

/**
 * OIDC {@link StateStore} that mirrors the user/session to `localStorage` and, when available,
 * {@link OfficeRuntime.storage} so tokens survive opening new workbooks or SharePoint documents.
 *
 * Security: when an Office storage write/remove fails partway through `remove`, this store
 * tombstones the key in memory and re-attempts removal lazily on `get`. Without this, a
 * subsequent `get` could resurrect a "logged out" user from `OfficeRuntime.storage` and copy
 * the stale token back into `localStorage`.
 */
export class InfloOidcStateStore implements StateStore {
  private readonly prefix: string;
  private readonly prefixLength: number;
  /** Keys that have been removed locally but whose Office-storage removal failed or is unconfirmed. */
  private readonly removedTombstones = new Set<string>();

  constructor(prefix = 'oidc.') {
    this.prefix = prefix;
    this.prefixLength = prefix.length;
  }

  private fullKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  async set(key: string, value: string): Promise<void> {
    const full = this.fullKey(key);
    window.localStorage.setItem(full, value);
    // A fresh write supersedes any pending tombstone for this key.
    this.removedTombstones.delete(full);
    const office = getOfficeRuntimeStorage();
    if (!office) {
      return;
    }
    try {
      await office.setItem(full, value);
    } catch (err) {
      // Office storage is best-effort; localStorage remains the fallback.
      console.warn(`InfloOidcStateStore.set(${full}) failed for OfficeRuntime.storage:`, err);
    }
  }

  async get(key: string): Promise<string | null> {
    const full = this.fullKey(key);
    const office = getOfficeRuntimeStorage();
    if (office) {
      // Tombstoned keys must never be re-read from Office storage; otherwise a failed remove
      // would let a stale OAuth user / refresh token re-emerge after logout.
      if (this.removedTombstones.has(full)) {
        try {
          await office.removeItem(full);
          this.removedTombstones.delete(full);
        } catch (err) {
          console.warn(`InfloOidcStateStore.get(${full}) re-removal of tombstoned key failed:`, err);
        }
        return null;
      }
      try {
        const fromOffice = await office.getItem(full);
        if (fromOffice != null) {
          if (window.localStorage.getItem(full) !== fromOffice) {
            window.localStorage.setItem(full, fromOffice);
          }
          return fromOffice;
        }
      } catch {
        // fall through to localStorage
      }
    }
    return window.localStorage.getItem(full);
  }

  async remove(key: string): Promise<string | null> {
    const full = this.fullKey(key);
    const existing = window.localStorage.getItem(full);
    window.localStorage.removeItem(full);
    const office = getOfficeRuntimeStorage();
    if (office) {
      try {
        await office.removeItem(full);
        this.removedTombstones.delete(full);
      } catch (err) {
        // Office removal failed — tombstone so we never serve the stale value back.
        this.removedTombstones.add(full);
        console.warn(`InfloOidcStateStore.remove(${full}) failed for OfficeRuntime.storage; tombstoned:`, err);
      }
    }
    return existing;
  }

  async getAllKeys(): Promise<string[]> {
    const keys = new Set<string>();
    const { prefix, prefixLength } = this;
    const ls = window.localStorage;

    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      if (k && k.startsWith(prefix)) {
        keys.add(k.slice(prefixLength));
      }
    }

    const office = getOfficeRuntimeStorage();
    if (office) {
      try {
        const remote = await office.getKeys();
        for (let i = 0; i < remote.length; i++) {
          const k = remote[i];
          if (k.startsWith(prefix) && !this.removedTombstones.has(k)) {
            keys.add(k.slice(prefixLength));
          }
        }
      } catch {
        // ignore
      }
    }

    return Array.from(keys);
  }
}
